import { useEffect, useState } from "react";
import { CiSearch } from "react-icons/ci";

import { Input } from "@/components/ui/input";
import { useStateContext } from "@/context/ContextProvider";
import { getAllOrders, getAllUsers } from "@/utils/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { LoggedInUser, UsersOrderData } from "@/types";
import groupOrdersByDate from "@/utils/groupOrdersByDate";
import calculateTotalRevenueForPeriod from "@/utils/calculateTotalRevenueForPeriod";
import CurrentOrderPopout from "@/components/Dashboard/CurrentOrderPopout";
import OverviewTab from "@/components/Dashboard/Tabs/OverviewTab";
import ActiveOrdersTab from "@/components/Dashboard/Tabs/ActiveOrdersTab";
import OrderHistoryTab from "@/components/Dashboard/Tabs/OrderHistoryTab";
import CustomerManagementTab from "@/components/Dashboard/Tabs/CustomerManagementTab";
import AnalyticsTab from "@/components/Dashboard/Tabs/AnalyticsTab";

const STORAGE_KEY = "dashboard_selected_tab";

const Dashboard = () => {
	const context = useStateContext();
	if (!context) {
		return null;
	}
	const { loggedInUser, loggedInUserToken } = context;

	const [ordersData, setOrdersData] = useState<UsersOrderData[]>([]);
	const [usersData, setUsersData] = useState<LoggedInUser[]>([]);
	const [currentOrder, setCurrentOrder] = useState<UsersOrderData | undefined>(
		undefined,
	);
	const [currentTab, setCurrentTab] = useState(() => {
		// Initialize from localStorage or default to "overview"
		return localStorage.getItem(STORAGE_KEY) || "overview";
	});
	const [searchQuery, setSearchQuery] = useState("");
	const [isLoading, setIsLoading] = useState(true);

	const handleSearchInputChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setSearchQuery(event.target.value);
	};

	const handleTabChange = (value: string) => {
		setCurrentTab(value);
		// Store the selected tab in localStorage
		localStorage.setItem(STORAGE_KEY, value);
	};

	useEffect(() => {
		setCurrentOrder(undefined);
	}, []);

	const groupedOrders = groupOrdersByDate(ordersData);

	const thisMonthsTotalRevenue = calculateTotalRevenueForPeriod(
		groupedOrders.thisMonth,
	);

	const thisYearsTotalRevenue = calculateTotalRevenueForPeriod(
		groupedOrders.thisYear,
	);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const orders: UsersOrderData[] = await getAllOrders(
					loggedInUserToken,
					loggedInUser,
				);

				const sortedOrdersByDate = orders.sort(
					(a, b) =>
						new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
				);

				setOrdersData(sortedOrdersByDate);

				const users = await getAllUsers(loggedInUserToken, loggedInUser);
				setUsersData(users);

				if (orders && users) {
					setIsLoading(false);
				}
			} catch (error) {
				console.log(error);
				setIsLoading(false);
			}
		};

		fetchData();
	}, [loggedInUser, loggedInUserToken]);

	return (
		<div className="flex min-h-screen w-full flex-col bg-muted/40">
			<div className="flex flex-col sm:gap-4 sm:py-4 sm:pl-14">
				<header className="sticky top-0 z-0 flex h-14 items-center gap-4 border-b bg-background px-4 sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6">
					<div className="relative ml-auto flex-1 md:grow-0">
						<CiSearch className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							type="search"
							placeholder="Search orders, customers, or dates..."
							className="w-full rounded-lg bg-background pl-8 md:w-[200px] lg:w-[320px]"
							onChange={handleSearchInputChange}
							value={searchQuery}
						/>
					</div>
				</header>
				<main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
					<Tabs
						defaultValue={currentTab}
						value={currentTab}
						className="w-full"
						onValueChange={handleTabChange}
					>
						<TabsList className="grid w-full grid-cols-5">
							<TabsTrigger value="overview">Overview</TabsTrigger>
							<TabsTrigger value="active">Active Orders</TabsTrigger>
							<TabsTrigger value="history">Order History</TabsTrigger>
							<TabsTrigger value="customers">Customers</TabsTrigger>
							<TabsTrigger value="analytics">Analytics</TabsTrigger>
						</TabsList>

						<TabsContent value="overview">
							<OverviewTab
								usersData={usersData}
								ordersData={ordersData}
								thisMonthsTotalRevenue={thisMonthsTotalRevenue}
								thisYearsTotalRevenue={thisYearsTotalRevenue}
								isLoading={isLoading}
								searchQuery={searchQuery}
							/>
						</TabsContent>

						<TabsContent value="active">
							<ActiveOrdersTab
								ordersData={ordersData}
								setCurrentOrder={setCurrentOrder}
								isLoading={isLoading}
								searchQuery={searchQuery}
							/>
						</TabsContent>

						<TabsContent value="history">
							<OrderHistoryTab
								ordersData={ordersData}
								setCurrentOrder={setCurrentOrder}
								isLoading={isLoading}
								searchQuery={searchQuery}
							/>
						</TabsContent>

						<TabsContent value="customers">
							<CustomerManagementTab
								usersData={usersData}
								ordersData={ordersData}
								isLoading={isLoading}
								searchQuery={searchQuery}
							/>
						</TabsContent>

						<TabsContent value="analytics">
							<AnalyticsTab
								ordersData={ordersData}
								usersData={usersData}
								isLoading={isLoading}
								searchQuery={searchQuery}
							/>
						</TabsContent>
					</Tabs>
				</main>
			</div>

			{currentOrder && (
				<CurrentOrderPopout
					currentOrder={currentOrder}
					setCurrentOrder={setCurrentOrder}
				/>
			)}
		</div>
	);
};

export default Dashboard;
